import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  PenTool, 
  ClipboardCheck, 
  Bell, 
  FileText, 
  ArrowRight, 
  User, 
  Github, 
  Zap,
  ChevronRight
} from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();

  // Navigation Data
  const tools = [
    {
      title: 'Documents',
      desc: 'Rich-text notes with instant cloud sharing and folder organization.',
      icon: <FileText size={22} />,
      path: '/editor',
      color: '#6366f1',
      stats: '12 Saved'
    },
    {
      title: 'Tasks',
      desc: 'Smart checklists and daily progress tracking for peak productivity.',
      icon: <ClipboardCheck size={22} />,
      path: '/tasks',
      color: '#10b981',
      stats: '4 Active'
    },
    {
      title: 'Reminders',
      desc: 'Timed notifications and alerts for your busy schedule.',
      icon: <Bell size={22} />,
      path: '/reminders',
      color: '#f59e0b',
      stats: '2 Today'
    }
  ];

  return (
    <div className="home-wrapper">
      {/* --- Sticky Header --- */}
      <header className="top-header">
        <div className="header-inner">
          <div className="brand-group" onClick={() => navigate('/')}>
            <img 
              src="https://ik.imagekit.io/filennky/NASQUARE/NASQUARE.png?updatedAt=1771564662383" 
              alt="NaSquare Logo" 
              className="brand-logo-img"
            />
            <span className="brand-text">NaSquare</span>
          </div>
          
          <div className="header-actions">
            <a href="https://github.com" target="_blank" rel="noreferrer" className="icon-circle-btn mobile-hide">
              <Github size={18} />
            </a>
            <button className="profile-btn">
              <User size={18} />
              <span className="mobile-hide">Account</span>
            </button>
          </div>
        </div>
      </header>

      <main className="main-content">
        {/* --- Hero Section --- */}
        <section className="hero-section">
          <div className="status-pill">
            <Zap size={14} className="zap-icon" />
            <span>Always synced, always ready</span>
          </div>
          <h1 className="hero-title">
            Your ideas, <br />
            <span className="text-gradient">perfectly squared.</span>
          </h1>
          <p className="hero-description">
            The all-in-one minimalist workspace designed for clarity. 
            Capture notes, manage tasks, and set reminders in seconds.
          </p>
          <div className="hero-cta-group">
            <button className="btn-primary" onClick={() => navigate('/editor')}>
              <PenTool size={18} />
              <span>Get Started</span>
              <ChevronRight size={18} />
            </button>
          </div>
        </section>

        {/* --- Interactive Dashboard Grid --- */}
        <section className="dashboard-grid">
          {tools.map((tool, idx) => (
            <div key={idx} className="tool-card" onClick={() => navigate(tool.path)}>
              {/* Subtle top accent line */}
              <div className="card-accent" style={{ background: tool.color }} />
              
              <div className="card-content">
                <div className="icon-box" style={{ color: tool.color, background: `${tool.color}15` }}>
                  {tool.icon}
                </div>
                <div className="text-box">
                  <h3>{tool.title}</h3>
                  <p>{tool.desc}</p>
                </div>
                
                <div className="card-meta">
                  <span className="stat-tag">{tool.stats}</span>
                  <div className="arrow-wrapper">
                    <ArrowRight size={18} className="arrow-icon" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </section>
      </main>

      {/* --- Footer --- */}
      <footer className="simple-footer">
        <div className="footer-line"></div>
        <p>© 2026 NaSquare • Designed for Focus</p>
      </footer>

      {/* --- CSS-in-JS Styles --- */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        :root {
          --primary: #6366f1;
          --bg: #fcfdfe;
          --text: #0f172a;
          --text-muted: #64748b;
          --card-bg: rgba(255, 255, 255, 0.85);
          --border: #f1f5f9;
        }

        * { 
          box-sizing: border-box; 
          margin: 0; 
          padding: 0; 
        }

        body {
          background-color: var(--bg);
          overflow-x: hidden;
        }

        .home-wrapper {
          min-height: 100vh;
          background: 
            radial-gradient(circle at 0% 0%, #eef2ff 0%, transparent 40%),
            radial-gradient(circle at 100% 100%, #fff7ed 0%, transparent 40%),
            var(--bg);
          font-family: 'Plus Jakarta Sans', sans-serif;
          color: var(--text);
        }

        /* --- Header --- */
        .top-header {
          position: sticky; 
          top: 0; 
          background: rgba(252, 253, 254, 0.7);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid var(--border);
          z-index: 1000;
        }
        .header-inner {
          max-width: 1200px; 
          margin: 0 auto; 
          height: 72px;
          display: flex; 
          align-items: center; 
          justify-content: space-between;
          padding: 0 24px;
        }
        .brand-group { 
          display: flex; 
          align-items: center; 
          gap: 12px; 
          cursor: pointer;
          transition: transform 0.2s;
        }
        .brand-group:hover { transform: scale(1.02); }
        
        .brand-logo-img {
          height: 36px;
          width: 36px;
          object-fit: contain;
          border-radius: 10px;
        }
        
        .brand-text { 
          font-size: 22px; 
          font-weight: 800; 
          letter-spacing: -0.8px; 
        }

        .header-actions { display: flex; align-items: center; gap: 12px; }
        
        .profile-btn {
          background: white; 
          border: 1px solid #e2e8f0; 
          padding: 8px 16px;
          border-radius: 100px; 
          display: flex; 
          align-items: center; 
          gap: 8px;
          font-weight: 600; 
          font-size: 14px; 
          cursor: pointer; 
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .profile-btn:hover { 
          border-color: var(--primary); 
          background: #f8fafc; 
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
        }
        
        .icon-circle-btn {
          background: white; 
          border: 1px solid #e2e8f0; 
          padding: 8px;
          border-radius: 50%; 
          cursor: pointer; 
          color: var(--text-muted);
          display: flex;
          align-items: center;
          text-decoration: none;
          transition: all 0.2s;
        }
        .icon-circle-btn:hover {
          color: var(--text);
          border-color: var(--text);
        }

        /* --- Hero Section --- */
        .main-content { 
          max-width: 1200px; 
          margin: 0 auto; 
          padding: 80px 24px; 
        }

        .hero-section { 
          text-align: center; 
          margin-bottom: 100px; 
        }

        .status-pill {
          display: inline-flex; 
          align-items: center; 
          gap: 8px;
          background: #f1f5f9; 
          color: #475569; 
          padding: 8px 20px;
          border-radius: 100px; 
          font-size: 13px; 
          font-weight: 700;
          margin-bottom: 32px;
          border: 1px solid rgba(255,255,255,0.6);
        }

        .zap-icon { color: var(--primary); fill: var(--primary); opacity: 0.8; }

        .hero-title {
          font-size: clamp(44px, 8vw, 78px); 
          line-height: 1.05;
          font-weight: 800; 
          letter-spacing: -3px; 
          margin-bottom: 24px;
        }
        .text-gradient {
          background: linear-gradient(135deg, #6366f1, #a855f7);
          -webkit-background-clip: text; 
          -webkit-text-fill-color: transparent;
        }
        .hero-description {
          font-size: 20px; 
          color: var(--text-muted);
          max-width: 620px; 
          margin: 0 auto 44px; 
          line-height: 1.6;
        }
        
        .btn-primary {
          background: var(--text); 
          color: white; 
          border: none;
          padding: 18px 40px; 
          border-radius: 18px; 
          font-size: 17px;
          font-weight: 700; 
          display: flex; 
          align-items: center; 
          gap: 12px;
          cursor: pointer; 
          transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          margin: 0 auto;
        }
        .btn-primary:hover { 
          transform: translateY(-4px); 
          box-shadow: 0 25px 40px rgba(15, 23, 42, 0.2); 
        }

        /* --- Card Grid --- */
        .dashboard-grid {
          display: grid; 
          grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
          gap: 32px;
        }
        .tool-card {
          background: var(--card-bg); 
          border: 1px solid var(--border);
          border-radius: 36px; 
          padding: 40px; 
          cursor: pointer;
          position: relative; 
          overflow: hidden;
          transition: all 0.5s cubic-bezier(0.2, 1, 0.3, 1);
          backdrop-filter: blur(10px);
        }
        .tool-card:hover {
          transform: translateY(-12px);
          border-color: var(--primary);
          background: white;
          box-shadow: 0 40px 80px rgba(0,0,0,0.06);
        }
        .card-accent {
          position: absolute; 
          top: 0; left: 0; 
          width: 100%; 
          height: 6px;
          opacity: 0.25;
        }
        .icon-box {
          width: 56px; 
          height: 56px; 
          border-radius: 16px;
          display: flex; 
          align-items: center; 
          justify-content: center;
          margin-bottom: 28px;
          transition: all 0.3s;
        }
        .tool-card:hover .icon-box { 
          transform: scale(1.15) rotate(-5deg); 
        }

        .text-box h3 { font-size: 26px; font-weight: 700; margin-bottom: 12px; }
        .text-box p { 
          font-size: 16px; 
          color: var(--text-muted); 
          line-height: 1.6; 
          margin-bottom: 32px; 
        }
        
        .card-meta {
          display: flex; 
          align-items: center; 
          justify-content: space-between;
          padding-top: 24px; 
          border-top: 1px solid var(--border);
        }
        .stat-tag { 
          font-size: 12px; 
          font-weight: 800; 
          color: var(--text-muted); 
          text-transform: uppercase; 
          letter-spacing: 1px; 
        }

        .arrow-wrapper {
          color: var(--text-muted);
          transition: all 0.3s;
        }
        .tool-card:hover .arrow-wrapper {
          color: var(--primary);
          transform: translateX(8px);
        }

        /* --- Footer --- */
        .simple-footer {
          text-align: center; 
          padding: 100px 24px 60px;
          font-size: 14px; 
          color: var(--text-muted);
        }
        .footer-line {
          height: 1px;
          background: var(--border);
          max-width: 160px;
          margin: 0 auto 28px;
        }

        /* --- Responsive Viewports --- */
        @media (max-width: 768px) {
          .mobile-hide { display: none; }
          .hero-section { margin-bottom: 60px; }
          .hero-title { font-size: 46px; letter-spacing: -2px; }
          .hero-description { font-size: 18px; }
          .main-content { padding: 40px 20px; }
          .dashboard-grid { grid-template-columns: 1fr; }
          .tool-card { padding: 32px; border-radius: 28px; }
        }
      `}</style>
    </div>
  );
};

export default HomePage;